/**
 * Ausgangswarteschlange.
 *
 * Grundsatz aus ADR 0001: Jede Erfassung wird **zuerst** örtlich geschrieben und
 * ist damit für den Nutzer abgeschlossen. Die Übertragung läuft danach im
 * Hintergrund. Wer im Keller steht, soll auf „Speichern" tippen und weitergehen
 * können — nicht auf einen Fortschrittsbalken warten.
 *
 * Zwei Wege führen hier hinein:
 *
 *   `enqueue`          – örtlich zuerst, ohne Sendeversuch. Für die Meldung mit
 *                        ihren Anhängen (`routes/report-new.tsx`).
 *   `sendeOderReihEin` – erst senden, nur bei Verbindungsabbruch einreihen. Für
 *                        Beiträge, Aufgaben, Halte und Prüfpunkte. Die
 *                        Begründung für den Unterschied steht dort.
 *
 * **Kein Workbox Background Sync.** ADR 0001 nennt ihn, der Dienstarbeiter in
 * `vite.config.ts` hält aber nur Bündel und Strukturdaten vor und fasst
 * schreibende Aufrufe nicht an. Das ist Absicht: Background Sync gibt es auf
 * iOS-Web nicht, und ein Weg, der auf der Hälfte der Geräte fehlt, wäre der
 * schlechtere Unterbau. Die Warteschlange läuft deshalb im Anwendungscode und
 * setzt bei `visibilitychange` und Netzrückkehr neu an (siehe `startQueue`).
 *
 * Drei Zustände sind für den Nutzer sichtbar (ADR 0001):
 *   local     – örtlich gespeichert
 *   sending   – wird übertragen
 *   confirmed – bestätigt
 *
 * Drei weitere sind Betriebszustände und werden als Hinweis angezeigt:
 *   failed    – gescheitert, wird mit wachsendem Abstand erneut versucht
 *   blocked   – der Endpunkt fehlt im Backend; ein erneuter Versuch wäre sinnlos
 *   partial   – der Vorgang ist angekommen, ein Anhang ist unwiederbringlich weg
 */
import { ApiError, isAuthenticated, request } from "@/api/client";
import type { MediaUploadResult } from "@/api/media";
import { t } from "@/i18n";
import { invalidateProbe, probe, subscribeNetwork } from "@/platform/network";
import {
  db,
  newId,
  type BlobKind,
  type BlobRecord,
  type DraftKind,
  type DraftRecord,
  type OutboxRecord,
  type OutboxState,
} from "@/platform/storage";

/** Erster Abstand nach einem Fehlversuch. */
const BACKOFF_BASE_MS = 2_000;
/** Obergrenze: Länger als fünf Minuten zu warten hilft niemandem mehr. */
const BACKOFF_MAX_MS = 5 * 60_000;
/** Takt der Hintergrundübertragung, solange etwas offen ist. */
const TICK_MS = 8_000;

export interface QueueCounts {
  local: number;
  sending: number;
  confirmed: number;
  failed: number;
  blocked: number;
  partial: number;
}

export interface QueueSnapshot {
  counts: QueueCounts;
  /**
   * Alles, was der Nutzer noch sehen muss — neueste zuerst.
   *
   * Nicht nur das Unbestätigte: Auch ein bestätigter Eintrag bleibt hier
   * stehen, solange ein ungelesener Hinweis des Servers an ihm hängt (etwa
   * „als Doublette gekennzeichnet"). Verschwände er sofort, wäre der Hinweis
   * nirgends mehr zu sehen — die Warteschlange hat keinen anderen Ort dafür.
   */
  pending: OutboxRecord[];
  /** Läuft gerade ein Durchlauf? */
  busy: boolean;
  lastRunAt: number | null;
}

const EMPTY_COUNTS: QueueCounts = {
  local: 0,
  sending: 0,
  confirmed: 0,
  failed: 0,
  blocked: 0,
  partial: 0,
};

let snapshot: QueueSnapshot = { counts: EMPTY_COUNTS, pending: [], busy: false, lastRunAt: null };
const listeners = new Set<() => void>();

export function getQueueSnapshot(): QueueSnapshot {
  return snapshot;
}

export function subscribeQueue(listener: () => void): () => void {
  listeners.add(listener);
  // Beim ersten Zuhörer den Stand einlesen: Nach einem Neustart der Anwendung
  // liegen Einträge von gestern in der Datenbank, die niemand angezeigt hätte.
  void refreshSnapshot();
  return () => listeners.delete(listener);
}

function emit(): void {
  for (const listener of listeners) listener();
}

async function refreshSnapshot(patch?: Partial<QueueSnapshot>): Promise<void> {
  const rows = await (await db()).outbox.orderBy("createdAt").reverse().toArray();
  const counts: QueueCounts = { ...EMPTY_COUNTS };
  for (const row of rows) counts[row.state] += 1;

  snapshot = {
    counts,
    pending: rows.filter((row) => row.state !== "confirmed" || (row.notices ?? []).length > 0),
    busy: patch?.busy ?? snapshot.busy,
    lastRunAt: patch?.lastRunAt ?? snapshot.lastRunAt,
  };
  emit();
}

// ---------------------------------------------------------------------------
// Einstellen
// ---------------------------------------------------------------------------

export interface QueuedAttachment {
  kind: BlobKind;
  mime: string;
  filename: string;
  data: Blob;
  meta?: Record<string, string | number | null>;
}

export interface EnqueueInput {
  kind: DraftKind;
  /** Kurzfassung für Listen, etwa die erste Zeile des Textes. */
  title: string;
  /** Zielpfad im Backend, relativ zum Schnittstellengrundpfad. */
  path: string;
  method?: OutboxRecord["method"];
  body: Record<string, unknown>;
  propertyId?: string | null;
  roomId?: string | null;
  attachments?: QueuedAttachment[];
  /**
   * Bereits vergebener Idempotenzschlüssel.
   *
   * Gebraucht von `sendeOderReihEin`: Dort ist der erste Sendeversuch schon
   * gelaufen, bevor eingereiht wird. Der Versuch kann den Server **erreicht**
   * haben und nur die Antwort verlorengegangen sein — im Keller ist genau das
   * der häufige Fall. Bekäme der Warteschlangeneintrag jetzt einen neuen
   * Schlüssel, wäre der zweite Anlauf für den Server eine neue Handlung, und
   * aus einem verlorenen Kommentar würden zwei gleiche.
   */
  idempotencyKey?: string;
}

export interface EnqueueResult {
  draftId: string;
  outboxId: string;
}

/**
 * Ausgang einer Handlung, die notfalls in die Warteschlange geht.
 *
 * Zwei Fälle, die der Nutzer **auseinanderhalten muss**: Entweder der Server hat
 * die Handlung angenommen und geantwortet, oder sie liegt im Gerät und wartet.
 * Beides „gespeichert" zu nennen wäre die eine Auskunft, die hier niemand
 * brauchen kann — wer glaubt, seine Aufgabe sei beim Server, ruft niemanden an,
 * wenn sie nie ankommt.
 *
 * Deshalb ein unterscheidender Typ und kein `T | null`: Der Übersetzer zwingt
 * jede Aufrufstelle, den zweiten Fall zu beantworten.
 */
export type Zustellung<T> =
  | { readonly zugestellt: true; readonly antwort: T }
  | { readonly zugestellt: false; readonly outboxId: string };

/**
 * Kurzmeldung für eine eingereihte Handlung — an jeder Stelle derselbe Wortlaut.
 *
 * Als gemeinsame Festlegung und nicht je Maske eigens formuliert: Wenn ein
 * Hausmeister denselben Sachverhalt an fünf Stellen unterschiedlich benannt
 * bekommt, lernt er keinen davon. Der Ton ist `warning` und nicht `success` —
 * es ist gutgegangen, aber es ist noch nicht fertig.
 */
export const EINGEREIHT_HINWEIS = {
  tone: "warning",
  title: t.queue.storedLocally,
  body: t.queue.storedLocallyHint,
} as const;

/**
 * Schreibt Entwurf, Anhänge und Übertragungsauftrag in einer Transaktion.
 *
 * Zusammen, nicht nacheinander: Bricht die Anwendung zwischen zwei Schreibungen
 * ab, bliebe sonst ein Auftrag ohne Foto oder ein Foto ohne Auftrag zurück —
 * beides müsste jemand von Hand aufräumen.
 */
export async function enqueue(input: EnqueueInput): Promise<EnqueueResult> {
  const database = await db();
  const draftId = newId();
  const outboxId = newId();
  const now = Date.now();

  const attachments = input.attachments ?? [];
  const blobs: BlobRecord[] = attachments.map((attachment) => ({
    id: newId(),
    draftId,
    kind: attachment.kind,
    mime: attachment.mime,
    filename: attachment.filename,
    bytes: attachment.data.size,
    data: attachment.data,
    meta: attachment.meta ?? {},
    createdAt: now,
  }));

  const draft: DraftRecord = {
    id: draftId,
    kind: input.kind,
    createdAt: now,
    title: input.title,
    payload: input.body,
    propertyId: input.propertyId ?? null,
    roomId: input.roomId ?? null,
  };

  const entry: OutboxRecord = {
    id: outboxId,
    draftId,
    kind: input.kind,
    title: input.title,
    path: input.path,
    method: input.method ?? "POST",
    body: input.body,
    // Der Schlüssel entsteht **ein einziges Mal** — hier, oder schon beim ersten
    // Sendeversuch in `sendeOderReihEin`. Jeder Wiederholversuch schickt
    // denselben, damit ein doppelt angekommener Aufruf keinen zweiten Datensatz
    // erzeugt (Entscheidung E-Z-04).
    idempotencyKey: input.idempotencyKey ?? newId(),
    serverId: null,
    state: "local",
    attempts: 0,
    createdAt: now,
    nextAttemptAt: now,
    lastError: null,
    notices: [],
    blobIds: blobs.map((blob) => blob.id),
  };

  await database.transaction("rw", database.drafts, database.blobs, database.outbox, async () => {
    await database.drafts.add(draft);
    if (blobs.length > 0) await database.blobs.bulkAdd(blobs);
    await database.outbox.add(entry);
  });

  await refreshSnapshot();
  // Sofort probieren, aber ohne darauf zu warten: Der Aufrufer ist fertig.
  void flush();
  return { draftId, outboxId };
}

/**
 * Handlung senden — und nur bei **Verbindungsabbruch** einreihen.
 *
 * ## Warum nicht „erst örtlich, dann übertragen" wie bei der Meldung
 *
 * ADR 0001 sagt: Jede Erfassung wird zuerst örtlich geschrieben. Für die Meldung
 * mit drei Fotos stimmt das uneingeschränkt — sie ist eine reine Erfassung,
 * niemand prüft sie im Moment des Absendens.
 *
 * Für die übrigen Handlungen wäre dieselbe Reihenfolge ein Rückschritt. Ein
 * Prüfpunkt hängt an einem laufenden Rundgang, eine Aufgabe an einem Objekt, ein
 * Kommentar an einem Vorgang — der Server lehnt ab, wenn der Rundgang nicht
 * läuft, das Recht fehlt oder die Eingabe unvollständig ist. Würde die
 * Oberfläche das grundsätzlich in die Warteschlange schieben, erführe der
 * Nutzer eine Ablehnung erst Minuten später und ohne die Maske, in der er sie
 * beheben könnte. Steht Netz zur Verfügung, ist die sofortige, ehrliche Antwort
 * die bessere.
 *
 * Eingereiht wird deshalb genau dann, wenn der Aufruf den Dienst **nie erreicht
 * hat** (`ApiError.isOffline`, Status 0). Nur dann ist ausgeschlossen, dass der
 * Server bereits eine Meinung hatte. Jeder andere Fehler — 403, 409, 422 — wird
 * unverändert weitergereicht und gehört in die Maske, nicht in die
 * Warteschlange.
 *
 * ## Der Schlüssel entsteht vor dem ersten Versuch
 *
 * `fetch` wirft auch dann, wenn die Anfrage angekommen ist und nur die Antwort
 * verlorenging. Der Warteschlangeneintrag erbt deshalb den Schlüssel des ersten
 * Versuchs — sonst wäre der zweite Anlauf für den Server eine neue Handlung.
 */
export async function sendeOderReihEin<T>(
  input: Omit<EnqueueInput, "idempotencyKey">,
): Promise<Zustellung<T>> {
  const idempotencyKey = newId();
  try {
    const antwort = await request<T>(input.path, {
      method: input.method ?? "POST",
      body: input.body,
      idempotencyKey,
    });
    return { zugestellt: true, antwort };
  } catch (error) {
    if (!(error instanceof ApiError) || !error.isOffline) throw error;

    const { outboxId } = await enqueue({ ...input, idempotencyKey });
    return { zugestellt: false, outboxId };
  }
}

// ---------------------------------------------------------------------------
// Übertragen
// ---------------------------------------------------------------------------

/**
 * Wachsender Abstand mit Streuung.
 *
 * Die Streuung ist kein Beiwerk: Kommt ein Bautrupp mit acht Geräten aus dem
 * Funkloch, würden sonst alle acht im selben Moment erneut senden.
 */
function backoffFor(attempts: number): number {
  const raw = BACKOFF_BASE_MS * 2 ** Math.max(0, attempts - 1);
  const capped = Math.min(raw, BACKOFF_MAX_MS);
  return Math.round(capped * (0.75 + Math.random() * 0.5));
}

/** Fehler, bei denen ein erneuter Versuch aussichtsreich ist. */
function isTransient(error: ApiError): boolean {
  if (error.isOffline) return true;
  if (error.status === 408 || error.status === 429) return true;
  return error.status >= 500;
}

let running = false;
let timer: number | null = null;

/**
 * Ergebnis eines Anhangsdurchlaufs.
 *
 * Drei Ausgänge statt einer Fehlermeldung, weil sie zu verschiedenen
 * Entscheidungen führen: `fehler` heißt „später noch einmal", `endgueltig`
 * heißt „nie wieder" und `hinweise` heißt „angekommen, aber der Server hat
 * etwas anzumerken". Bisher gab es nur die eine Fehlermeldung — und der
 * häufigste Fall, der fehlende Anhang, hatte darin gar keinen Platz.
 */
interface AnhangErgebnis {
  /** Vorübergehender Fehler — ein erneuter Versuch ist aussichtsreich. */
  fehler: string | null;
  /**
   * Endgültiger Verlust, ausformuliert für die Anzeige. Wiederholen hilft nicht
   * mehr; der Nutzer muss erfahren, was fehlt, und es neu erfassen.
   */
  endgueltig: string | null;
  /** Anmerkungen des Servers zu den übertragenen Anhängen, in Anzeigeform. */
  hinweise: string[];
}

/**
 * Lädt die Anhänge eines Auftrags hoch.
 *
 * Der Auftrag gilt erst dann als bestätigt, wenn **auch** die Anhänge
 * angekommen sind — ein Vorgang ohne sein Beweisfoto ist auf einer Baustelle
 * die halbe Information, und „bestätigt" wäre dafür die falsche Auskunft.
 * Fehlt ein Anhang endgültig, endet der Eintrag deshalb auf `partial` und nicht
 * auf `confirmed`.
 *
 * Jeder Anhang wird **einzeln** aus dem Gerät entfernt, sobald er angekommen
 * ist. Erst am Ende zu löschen war bequemer, hatte aber einen Preis: Scheiterte
 * Foto 3 von 5, lud der nächste Versuch die Fotos 1 und 2 erneut hoch. Der
 * Server nimmt sie an und kennzeichnet sie als Doublette — die Nachweislage
 * wird dadurch nicht besser, nur unübersichtlicher.
 *
 * Ein `Idempotency-Key` je Anhang wäre der sauberere Weg, ist aber derzeit
 * wirkungslos: `POST /media` hängt im Backend nicht an `IdempotencyDep` und
 * würde die Kopfzeile stillschweigend ignorieren (siehe `backend/app/api/
 * v1/media.py`). Solange das so ist, ist das Aufräumen im Gerät die einzige
 * wirksame Sperre gegen Doubletten.
 */
async function sendeAnhaenge(entry: OutboxRecord, issueId: string | null): Promise<AnhangErgebnis> {
  const ergebnis: AnhangErgebnis = { fehler: null, endgueltig: null, hinweise: [] };
  if (entry.blobIds.length === 0) return ergebnis;

  const database = await db();
  const blobs = await database.blobs.bulkGet(entry.blobIds);

  // Objekt und Raum stehen am Entwurf, nicht am Übertragungsauftrag.
  const entwurf = entry.draftId !== null ? await database.drafts.get(entry.draftId) : undefined;
  const objektId = entwurf?.propertyId ?? null;
  const raumId = entwurf?.roomId ?? null;

  if (objektId === null) {
    // Kein vorübergehender Fehler: Die Objektzuordnung steht am Entwurf und
    // ändert sich nicht mehr. Bisher lief das in eine endlose Wiederholung, bei
    // der jedes Mal der ganze Vorgang mitgeschickt wurde — und die Oberfläche
    // zeigte dabei dauerhaft „Erneuter Versuch" statt „so wird das nichts".
    ergebnis.endgueltig =
      `Die Meldung ist übertragen, aber ${entry.blobIds.length} ` +
      `${entry.blobIds.length === 1 ? "Anhang lässt" : "Anhänge lassen"} sich nicht zuordnen: ` +
      "Am Entwurf fehlt das Objekt. Bitte die Anhänge neu erfassen.";
    return ergebnis;
  }

  // Ohne Vorgangskennung wird trotzdem hochgeladen — der Anhang hängt dann an
  // Objekt und Raum und ist dort auffindbar. Ihn zurückzuhalten hieße, ein
  // vorhandenes Beweisfoto wegen einer fehlenden Verknüpfung zu verwerfen.
  if (issueId === null) {
    ergebnis.hinweise.push(
      "Die Anhänge wurden ohne Bezug zur Meldung übertragen und hängen nur an Objekt und Raum.",
    );
  }

  // Was angekommen ist, fällt hier heraus; der Rest bleibt im Gerät stehen.
  const verbleibend = [...entry.blobIds];
  const verloren: string[] = [];

  for (const [index, blob] of blobs.entries()) {
    if (blob === undefined) {
      // Der Datensatz ist weg — IndexedDB unter Speicherdruck geräumt oder eine
      // abgebrochene Transaktion. Wiederholen holt ihn nicht zurück. Ihn hier
      // stillschweigend zu überspringen war der eigentliche Schaden: Der
      // Eintrag ging danach auf „bestätigt", und niemand erfuhr je, dass das
      // Foto fehlt.
      verloren.push(`Anhang ${index + 1}`);
      // Die Kennung bleibt in `blobIds` stehen, bis der Verlust gemeldet ist —
      // gelöscht würde selbst die Spur des Fehlenden verschwinden.
      continue;
    }

    const formular = new FormData();
    // Den MIME-Typ ausdrücklich mitgeben: Ein Blob aus der Aufnahme trägt ihn
    // zwar, aber nach dem Zwischenspeichern in IndexedDB kann er verloren sein.
    // Ohne Typ lehnt die Prüfung beim Hochladen ab, weil sie ihn gegen die
    // Liste erlaubter Arten hält.
    const datei = new File([blob.data], blob.filename, {
      type: blob.mime || (blob.kind === "audio" ? "audio/webm" : "image/jpeg"),
    });
    formular.append("file", datei, blob.filename);
    formular.append("property_id", objektId);
    // **`audio`, nicht `voice_note`.** Die Aufzählung im Backend kennt nur
    // photo, video, audio und drawing — mit einem erfundenen Wert antwortete der
    // Upload mit 422 („Die Eingabe ist unvollständig oder fehlerhaft"), während
    // der Vorgang selbst schon angelegt war. Die Sprachnotiz blieb im Gerät.
    formular.append("media_type", blob.kind === "audio" ? "audio" : "photo");
    // Die Bauablaufphase ist bei Foto und Video Pflicht, bei einer Aufnahme
    // nicht. Auf der Baustelle kennt niemand die Phase im Moment des Auslösens,
    // deshalb die neutralste: Sie sagt „während der Arbeit", nicht „Zustand vor
    // Beginn" — letzteres wäre eine Behauptung über den Bauablauf, die niemand
    // geprüft hat.
    if (blob.kind !== "audio") formular.append("phase", "during_work");

    // Wortlaut einer Sprachaufnahme mitschicken, wenn er vorliegt.
    //
    // Ohne ihn liegt die Aufnahme stumm neben ihrem Text: Der Wortlaut steht im
    // Vorgang, die Aufnahme selbst ist nicht durchsuchbar, und ob beides
    // zusammengehört, kann später niemand mehr prüfen. Bei einer Meldung, auf
    // die sich eine Mängelrüge stützt, ist genau das die Frage.
    const wortlaut = blob.meta?.transcript;
    if (typeof wortlaut === "string" && wortlaut.trim() !== "") {
      formular.append("transcript", wortlaut);
      const sprache = blob.meta?.transcript_language;
      if (typeof sprache === "string" && sprache !== "") {
        formular.append("transcript_language", sprache);
      }
      const verlaesslichkeit = blob.meta?.transcript_confidence;
      if (typeof verlaesslichkeit === "string" && verlaesslichkeit !== "") {
        formular.append("transcript_confidence", verlaesslichkeit);
      }
    }
    if (raumId !== null) formular.append("room_id", raumId);
    if (issueId !== null) formular.append("issue_id", issueId);
    // Die Kennung des Geräteentwurfs mitschicken. Das Backend nimmt sie an und
    // legt einen Index darauf — damit lässt sich später beantworten, welche
    // Aufnahmen aus derselben Erfassung stammen. Ohne sie steht am Server nur
    // ein Bündel Dateien ohne gemeinsame Herkunft.
    if (entry.draftId !== null) formular.append("client_draft_id", entry.draftId);

    try {
      const antwort = await request<MediaUploadResult>("/media", {
        method: "POST",
        body: formular,
      });

      // Die Antwort auswerten, statt sie wegzuwerfen: Sie sagt, ob der Server
      // die Aufnahme als Doublette geführt hat und was er sonst anzumerken hat
      // (verworfene Standortdaten, unscharfes Bild, Aufnahmezeit weit vor dem
      // Upload). Der Online-Weg zeigt genau das an; aus der Warteschlange
      // heraus blieb es bisher unsichtbar.
      if (antwort?.duplicate_of_media_id != null) {
        ergebnis.hinweise.push(
          `„${blob.filename}" lag bereits vor und wurde als Doublette gekennzeichnet.`,
        );
      }
      for (const hinweis of antwort?.notices ?? []) {
        // `info` ist Beiwerk (etwa „EXIF entfernt"), `warning` und höher nicht.
        if (hinweis.severity === "info") continue;
        ergebnis.hinweise.push(`„${blob.filename}": ${hinweis.message}`);
      }

      // Sofort aus dem Gerät entfernen — dieser Anhang ist beim Server. Nur was
      // noch nicht angekommen ist, bleibt liegen; so lädt ein Wiederholversuch
      // ausschließlich den Rest hoch.
      const stelle = verbleibend.indexOf(blob.id);
      if (stelle >= 0) verbleibend.splice(stelle, 1);
      await database.blobs.delete(blob.id);
      await database.outbox.update(entry.id, { blobIds: [...verbleibend] });
    } catch (error) {
      const meldung = error instanceof Error ? error.message : String(error);
      ergebnis.fehler = `Anhang „${blob.filename}" konnte nicht übertragen werden: ${meldung}`;
      return ergebnis;
    }
  }

  if (verloren.length > 0) {
    ergebnis.endgueltig =
      `Die Meldung ist übertragen, aber ${verloren.length} ` +
      `${verloren.length === 1 ? "Anhang ist" : "Anhänge sind"} im Gerät nicht mehr auffindbar ` +
      `(${verloren.join(", ")}). Nachreichen ist nicht möglich — bitte das Foto ` +
      "beziehungsweise die Aufnahme neu erfassen.";
  }

  return ergebnis;
}

async function processOne(entry: OutboxRecord): Promise<void> {
  const database = await db();
  await database.outbox.update(entry.id, { state: "sending" satisfies OutboxState });
  await refreshSnapshot();

  try {
    // Ist der Vorgang schon angelegt, wird er **nicht** noch einmal geschickt.
    // Genau hier entstand sonst die Doppelung: Nach einem Fehlschlag beim
    // dritten Foto wiederholte der nächste Durchlauf den ganzen Auftrag. Seit
    // `POST /issues` den Idempotenzschlüssel auswertet, käme dabei zwar kein
    // zweiter Vorgang mehr heraus — aber eine überflüssige Anfrage, und die
    // kostet im Keller genau die Verbindung, auf die es ankommt.
    let vorgangId = entry.serverId ?? null;
    if (vorgangId === null) {
      const antwort = await request<{ id?: string }>(entry.path, {
        method: entry.method,
        body: entry.body,
        idempotencyKey: entry.idempotencyKey,
      });
      vorgangId = antwort?.id ?? null;
      // Sofort festhalten, vor dem ersten Anhang. Bricht die Anwendung mitten
      // im Hochladen ab, weiß der nächste Start, dass der Vorgang steht.
      await database.outbox.update(entry.id, { serverId: vorgangId });
    }

    // Anhänge erst **nach** dem Vorgang: Sie brauchen dessen Kennung, um
    // zugeordnet zu werden. Vorher hochgeladen wären sie herrenlose Dateien im
    // Speicher — und genau das ist bisher passiert, nur ohne Upload: Fotos und
    // Sprachaufnahmen blieben im Gerät liegen und wurden beim Verwerfen
    // gelöscht.
    const anhaenge = await sendeAnhaenge(entry, vorgangId);

    // Ein vorübergehender Fehler geht in die Wiederholung — der Vorgang selbst
    // wird dabei nicht noch einmal geschickt (`serverId` steht jetzt).
    if (anhaenge.fehler !== null) {
      await database.outbox.update(entry.id, {
        state: "failed" satisfies OutboxState,
        attempts: entry.attempts + 1,
        nextAttemptAt: Date.now() + backoffFor(entry.attempts + 1),
        lastError: anhaenge.fehler,
        notices: anhaenge.hinweise,
      });
      return;
    }

    // Ein fehlender Anhang ist kein Fall für die Wiederholung: Der Datensatz
    // ist aus dem Gerätespeicher verschwunden und kommt nicht zurück. Der
    // Eintrag bleibt sichtbar stehen, bis der Nutzer den Verlust gesehen hat.
    if (anhaenge.endgueltig !== null) {
      await database.outbox.update(entry.id, {
        state: "partial" satisfies OutboxState,
        lastError: anhaenge.endgueltig,
        notices: anhaenge.hinweise,
      });
      return;
    }

    await database.outbox.update(entry.id, {
      state: "confirmed" satisfies OutboxState,
      attempts: entry.attempts,
      nextAttemptAt: entry.nextAttemptAt,
      lastError: null,
      notices: anhaenge.hinweise,
    });
    return;
  } catch (error) {
    if (!(error instanceof ApiError)) {
      await database.outbox.update(entry.id, {
        state: "failed" satisfies OutboxState,
        attempts: entry.attempts + 1,
        nextAttemptAt: Date.now() + backoffFor(entry.attempts + 1),
        lastError: error instanceof Error ? error.message : String(error),
      });
      return;
    }

    // Der Endpunkt gibt es noch nicht. Wiederholen würde nur Strom kosten; der
    // Eintrag bleibt sichtbar erhalten und geht raus, sobald das Backend liefert.
    if (error.isUnimplemented) {
      await database.outbox.update(entry.id, {
        state: "blocked" satisfies OutboxState,
        attempts: entry.attempts + 1,
        lastError: `Der Endpunkt ${entry.method} ${entry.path} ist im Backend noch nicht vorhanden.`,
      });
      return;
    }

    // Abgelaufene Anmeldung ist kein Fehler des Eintrags: Zustand zurücksetzen,
    // damit kein Versuch verbraucht wird, und den Durchlauf beenden.
    if (error.status === 401 || error.status === 403) {
      await database.outbox.update(entry.id, {
        state: "local" satisfies OutboxState,
        lastError: "Wartet auf eine gültige Anmeldung.",
      });
      throw error;
    }

    if (isTransient(error)) {
      const attempts = entry.attempts + 1;
      await database.outbox.update(entry.id, {
        state: "failed" satisfies OutboxState,
        attempts,
        nextAttemptAt: Date.now() + backoffFor(attempts),
        lastError: error.message,
      });
      return;
    }

    // Alles andere (422 fehlerhafte Eingabe, 409 Konflikt) wird durch
    // Wiederholen nie richtig. Der Eintrag bleibt liegen und braucht eine
    // Entscheidung des Nutzers.
    await database.outbox.update(entry.id, {
      state: "blocked" satisfies OutboxState,
      attempts: entry.attempts + 1,
      lastError: error.message,
    });
  }
}

/**
 * Arbeitet alles ab, was fällig ist. Mehrere gleichzeitige Aufrufe sind
 * unschädlich — der zweite kehrt sofort zurück.
 */
export async function flush(): Promise<void> {
  if (running) return;
  if (!isAuthenticated()) return;
  if (!(await probe())) return;

  running = true;
  await refreshSnapshot({ busy: true });

  try {
    const now = Date.now();
    const due = (await (await db()).outbox.toArray()).filter(
      (entry) =>
        (entry.state === "local" || entry.state === "failed" || entry.state === "sending") &&
        entry.nextAttemptAt <= now,
    );

    for (const entry of due) {
      try {
        await processOne(entry);
      } catch {
        // Anmeldung abgelaufen: Rest des Durchlaufs abbrechen, sonst laufen alle
        // Einträge in denselben Fehler.
        break;
      }
    }
  } finally {
    running = false;
    await refreshSnapshot({ busy: false, lastRunAt: Date.now() });
    scheduleNext();
  }
}

function scheduleNext(): void {
  if (timer !== null) return;
  const hasOpen = snapshot.pending.some(
    (entry) => entry.state === "local" || entry.state === "failed",
  );
  if (!hasOpen) return;

  timer = window.setTimeout(() => {
    timer = null;
    void flush();
  }, TICK_MS);
}

let started = false;

/**
 * Hängt die Warteschlange an Netz- und Sichtbarkeitsereignisse.
 *
 * `visibilitychange` ist hier wichtiger als es klingt: Auf dem Telefon friert
 * der Hintergrund-Zeitgeber ein, sobald die Anwendung weggewischt wird. Ohne den
 * Auslöser beim Zurückkehren stünde die Warteschlange still, obwohl längst WLAN da ist.
 */
export function startQueue(): () => void {
  if (started) return () => undefined;
  started = true;

  const unsubscribeNetwork = subscribeNetwork((online) => {
    invalidateProbe();
    if (online) void flush();
  });

  const onVisible = (): void => {
    if (document.visibilityState === "visible") {
      invalidateProbe();
      void flush();
    }
  };
  document.addEventListener("visibilitychange", onVisible);

  void flush();

  return () => {
    unsubscribeNetwork();
    document.removeEventListener("visibilitychange", onVisible);
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
    started = false;
  };
}

/** Wiederholt einen Eintrag ohne Wartezeit — für den Knopf „Jetzt senden". */
export async function retryNow(outboxId: string): Promise<void> {
  await (await db()).outbox.update(outboxId, {
    state: "local" satisfies OutboxState,
    nextAttemptAt: Date.now(),
    lastError: null,
  });
  invalidateProbe();
  await refreshSnapshot();
  await flush();
}

/** Verwirft einen Eintrag samt Entwurf und Anhängen. */
export async function discard(outboxId: string): Promise<void> {
  const database = await db();
  const entry = await database.outbox.get(outboxId);
  if (!entry) return;

  await database.transaction("rw", database.drafts, database.blobs, database.outbox, async () => {
    if (entry.blobIds.length > 0) await database.blobs.bulkDelete(entry.blobIds);
    if (entry.draftId) await database.drafts.delete(entry.draftId);
    await database.outbox.delete(outboxId);
  });
  await refreshSnapshot();
}

/**
 * Räumt bestätigte Einträge ab, die älter sind als die angegebene Frist.
 *
 * Die Anhänge sind der Grund: Ein bestätigtes Foto belegt weiter Speicherplatz,
 * und der Browser löscht bei Platznot die ganze Datenbank — auch die Einträge,
 * die noch nicht übertragen sind.
 */
export async function pruneConfirmed(olderThanMs = 24 * 60 * 60 * 1000): Promise<number> {
  const database = await db();
  const cutoff = Date.now() - olderThanMs;
  const stale = (await database.outbox.where("state").equals("confirmed").toArray()).filter(
    (entry) => entry.createdAt < cutoff,
  );

  for (const entry of stale) await discard(entry.id);
  return stale.length;
}
